import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus, ArrowUpRight } from 'lucide-react';
import { scrollToSection } from '../lib/sectionNavigation';

const faqs = [
  {
    question: 'What is ADX Hub?',
    answer:
      'ADX Hub is a platform that supports AAS data exchange between companies. It provides identity, discovery, validation, Submodel template access, and modeling services. Companies keep their existing domain systems, while ADX Hub is used as the trust and integration layer around them.',
  },
  {
    question: 'How is data exchange done using ADX Hub?',
    answer:
      'A data consumer searches for an asset in ADX Hub, and ADX Hub finds the right data provider and the correct AAS endpoints. ADX Hub then handles trusted access between both companies so only approved requests go through. Once trust checks are passed, the requested AAS information is retrieved from the provider systems and returned to the consumer in a usable format.',
  },
  {
    question: 'How does a data provider get started?',
    answer:
      'A data provider starts by onboarding its organization in the trustlist, registering Discovery/Registry/Repository endpoints, and ensuring AAS APIs are reachable behind the provider gateway. The provider then deploys the ADX Hub token exchange service (STS) in its own infrastructure and configures it with its IdP. The IdP receives the federated F-X token, validates it against the trustlist, and exchanges it for an internal token that is used to access provider AAS services.',
  },
  {
    question: 'How does a data consumer get started?',
    answer:
      'A data consumer is onboarded in the trustlist and receives credentials to access ADX Hub services. The consumer then uses Discovery to find providers and AAS endpoints for a target asset, and can also use the integrated AAS client to discover and fetch AAS payloads.',
  },
  {
    question: 'Does ADX Hub see the exchanged data during exchange?',
    answer:
      'ADX Hub manages discovery, trust, and access orchestration for the exchange flow. It can broker metadata and return assembled AAS responses to authorized consumers, while the source-of-truth business data remains in provider systems. Access is controlled through token validation, trustlist checks, and provider IAM policies. In short, ADX Hub coordinates secure exchange, and providers retain ownership and control of their data.',
  },
];

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <section id="faq" className="bg-white py-16 md:py-20" data-testid="faq-section">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_1.3fr] lg:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.4 }}
            className="lg:sticky lg:top-28 lg:self-start"
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#0028CD]">
              FAQ
            </p>
            <h2 className="mt-3 text-3xl font-bold tracking-tight text-[#0028CD] md:text-4xl">
              Frequently asked questions
            </h2>
            <div className="mt-8 rounded-2xl border border-slate-200 bg-[#f8fafc] p-5">
              <p className="text-[14px] font-semibold text-slate-900">
                Still have questions?
              </p>
              <p className="mt-1 text-[13px] leading-relaxed text-slate-500">
                Talk to our team about integration.
              </p>
              <button
                type="button"
                onClick={() => scrollToSection('contact-form')}
                className="mt-4 inline-flex items-center gap-1.5 border-0 bg-transparent p-0 text-[13px] font-semibold text-[#0028CD] hover:underline"
              >
                Contact us
                <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2.5} />
              </button>
            </div>
          </motion.div>

          <div className="divide-y divide-slate-100">
            {faqs.map((faq, index) => {
              const isOpen = openIndex === index;
              return (
                <div key={faq.question}>
                  <button
                    type="button"
                    onClick={() => setOpenIndex(isOpen ? -1 : index)}
                    aria-expanded={isOpen}
                    aria-controls={`faq-answer-${index}`}
                    id={`faq-question-${index}`}
                    className="group flex w-full items-center justify-between gap-6 py-6 text-left focus:outline-none"
                  >
                    <span
                      className={`text-[15.5px] font-semibold leading-snug transition-colors ${
                        isOpen ? 'text-[#0028CD]' : 'text-slate-900 group-hover:text-[#0028CD]'
                      }`}
                    >
                      {faq.question}
                    </span>
                    <span
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition-colors ${
                        isOpen
                          ? 'border-[#0028CD] bg-[#0028CD] text-white'
                          : 'border-slate-200 text-slate-500 group-hover:border-[#0028CD] group-hover:text-[#0028CD]'
                      }`}
                    >
                      {isOpen ? (
                        <Minus className="h-3.5 w-3.5" strokeWidth={2.5} />
                      ) : (
                        <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
                      )}
                    </span>
                  </button>

                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        key="answer"
                        id={`faq-answer-${index}`}
                        role="region"
                        aria-labelledby={`faq-question-${index}`}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        <p className="pb-6 pr-10 text-[14px] leading-[1.75] text-slate-500">
                          {faq.answer}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
